import { useSelector } from 'react-redux';
import { Field, NumericInput, TextOutput, RadioGroup, Checkbox } from '../../../../Core';
import { PageSection, ListFieldSection } from '../../../../Layout';
import { WorksheetAssociatedTestSection } from '../../../Core';
import React from 'react';
import { withInjectedProps } from '../../../../Core/InjectedPropsContext';
import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { Guid } from 'Lib/Guid';
import { combine } from 'Lib/Hooks/Selectors';
import { hasValue } from 'Lib/Any';
import externalTestMethods from '../../../ExternalTestMethods';

let OversizeCorrectionSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const visible = (useSelector(createWorksheetSelector!('Preparation.OversizeCorrectionRequired')) as boolean) ?? false;
  const importedOversizeMoistureTestId = useSelector(
    createWorksheetSelector!('Preparation.SpecificGravityCoarse.AssociatedOversizeMoisture.ImportedTest.Id')
  ) as Guid;
  const importedSpecificGravityCoarseTestId = useSelector(
    createWorksheetSelector!('Preparation.SpecificGravityCoarse.AssociatedSpecificGravityCoarse.ImportedTest.Id')
  ) as Guid;

  const useAbsorptionAsOversizeMoisture =
    (useSelector(
      createWorksheetSelector!('Preparation.SpecificGravityCoarse.UseAbsorptionAsOversizeMoisture')
    ) as boolean) ?? false;

  const hideMaterialTakenAs = useSelector(
    combine(
      [
        'Preparation.AssociatedOversizeDetermination.ImportedTest',
        'Preparation.AssociatedOversizeDetermination.ExternalTest',
      ].map((n) => createWorksheetSelector!(n)),
      (imported, external) => hasValue(imported) || hasValue(external)
    )
  );

  return !visible ? null : (
    <PageSection heading="ASTM D4718 Oversize Correction Data">
      <WorksheetAssociatedTestSection
        heading="ASTM C127 Relative Density (Specific Gravity) and Absorption of Coarse Aggregate"
        label="Determined by:"
        name="Preparation.SpecificGravityCoarse.AssociatedSpecificGravityCoarse"
        testType="Specific Gravity Coarse"
        associatedTestModel={{
          measured: {
            measuredTestSetup: (
              <ListFieldSection visible={!hideMaterialTakenAs} wide>
                <Field
                  input={<RadioGroup />}
                  label="Material Taken as"
                  name="Preparation.SpecificGravityCoarse.MeasuredSpecificGravityCoarseMaterialSource"
                  options={[
                    { label: 'Full sample retained on oversize sieve', value: 'oversize_full_sample' },
                    { label: 'Sub sample of material retained on oversize sieve', value: 'oversize_sub_sample' },
                  ]}
                />
              </ListFieldSection>
            ),
          },
          imported: {
            importedTestResults: (
              <ListFieldSection>
                <Field
                  input={<TextOutput />}
                  label="Specific Gravity (Oven Dry)"
                  name="Preparation.SpecificGravityCoarse.SpecificGravityCoarseResults.SpecificGravityOvenDry"
                  emph
                />
                <Field
                  input={<TextOutput />}
                  label="Specific Gravity (SSD)"
                  name="Preparation.SpecificGravityCoarse.SpecificGravityCoarseResults.SpecificGravitySSD"
                  emph
                />
                <Field
                  input={<TextOutput />}
                  label="Apparent Specific Gravity"
                  name="Preparation.SpecificGravityCoarse.SpecificGravityCoarseResults.ApparentSpecificGravity"
                  emph
                />
                <Field
                  input={<TextOutput />}
                  label="Absorption (%)"
                  name="Preparation.SpecificGravityCoarse.SpecificGravityCoarseResults.Absorption"
                  emph
                />
              </ListFieldSection>
            ),
            importModel: {
              Id: importedSpecificGravityCoarseTestId,
            },
          },
          external: {
            testMethods: [externalTestMethods.specificGravity.ASTM_C127],
            externalTestResults: (
              <ListFieldSection single>
                <Field
                  input={<NumericInput />}
                  label="Specific Gravity (Oven Dry)"
                  name="Preparation.SpecificGravityCoarse.AssociatedSpecificGravityCoarse.ExternalTest.Results.SpecificGravityOvenDry"
                />
                <Field
                  input={<NumericInput />}
                  label="Absorption (%)"
                  name="Preparation.SpecificGravityCoarse.AssociatedSpecificGravityCoarse.ExternalTest.Results.Absorption"
                />
              </ListFieldSection>
            ),
          },
        }}
      />
      <Field
        input={<Checkbox />}
        label="Use Absorption as Coarse Material Water Content"
        name="Preparation.SpecificGravityCoarse.UseAbsorptionAsOversizeMoisture"
        emph
      />
      {!useAbsorptionAsOversizeMoisture && (
        <WorksheetAssociatedTestSection
          heading=""
          label="Coarse Material Water Content Determined by:"
          name="Preparation.SpecificGravityCoarse.AssociatedOversizeMoisture"
          testType="Moisture Content"
          associatedTestModel={{
            measured: true,
            imported: {
              importedTestResults: (
                <ListFieldSection>
                  <Field
                    input={<TextOutput />}
                    label="Water Content (%)"
                    name="Preparation.ScreeningASTM.OversizeMoistureContent"
                    emph
                  />
                </ListFieldSection>
              ),
              importModel: {
                Id: importedOversizeMoistureTestId,
              },
            },
            external: {
              permitOtherTestMethods: true,
              testMethods: [externalTestMethods.moistureContent.ASTM_D2216],
              externalTestResults: (
                <ListFieldSection>
                  <Field
                    input={<NumericInput />}
                    label="Water Content (%)"
                    name="Preparation.SpecificGravityCoarse.AssociatedOversizeMoisture.ExternalTest.Results.MoistureContentPercent"
                  />
                </ListFieldSection>
              ),
            },
          }}
        />
      )}
    </PageSection>
  );
};

OversizeCorrectionSection = withInjectedProps(OversizeCorrectionSection);

export { OversizeCorrectionSection };
export default OversizeCorrectionSection;
